import {useConfig} from '@aboutyou/config';
import {useTranslation} from '@aboutyou/i18n';
import {usePrivacyPolicyRoute} from '@aboutyou/router';
import {emailRegex} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {useCallback, useState} from 'react';
import {useForm} from 'react-hook-form';
import {COLORS} from '../../../constants/colors';
import {Button} from '../../atoms/Buttons/Button';
import {Disclaimer} from '../../atoms/Disclaimer/Disclaimer';
import {RadioButton} from '../../atoms/RadioButton/RadioButton';
import {RadioGroup} from '../../atoms/RadioButton/RadioGroup';
import {Text} from '../../atoms/Text/Text';
import {TextInput} from '../../atoms/TextInput/TextInput';

interface SubscriptionFormData {
  email: string;
  gender: 'male' | 'female';
}

export type FunctionHandleNewsletterSubscribe = (
  formData: SubscriptionFormData,
  reset: () => void,
) => void;

interface NewsLetterSubscriptionProps {
  onNewsletterSubscribe: FunctionHandleNewsletterSubscribe;
  children?: React.ReactNode;
}

export const NewsLetterSubscription: React.FC<NewsLetterSubscriptionProps> = ({
  onNewsletterSubscribe,
}) => {
  const {t} = useTranslation();
  const subscriptionForm = useForm<SubscriptionFormData>({
    defaultValues: {
      email: '',
      gender: 'female',
    },
    mode: 'onSubmit',
  });
  const {customerEmail} = useConfig();

  const [newUserFormInstanceKey, setNewUserFormInstanceKey] = useState(0);
  const resetFormState = useCallback(() => {
    setNewUserFormInstanceKey(newUserFormInstanceKey + 1);
    subscriptionForm.reset();
  }, [newUserFormInstanceKey, subscriptionForm]);
  const privacyPolicyLink = usePrivacyPolicyRoute();

  const submitHandler = (data: SubscriptionFormData) => {
    onNewsletterSubscribe(data, resetFormState);
  };

  const privacyPolicyLinkSection = `${privacyPolicyLink}#section3.3.2.`;

  return (
    <Wrapper data-testid="footer-newsletter">
      <StyledText size="px24" weight="bold">
        {t("Don't miss a thing!")}
      </StyledText>

      <StyledText size="px14" weight="bold">
        {t('Sign up for the newsletter & receive exclusive offers')}
      </StyledText>
      <form
        autoComplete="on"
        onSubmit={subscriptionForm.handleSubmit(submitHandler)}
        method="POST"
        key={newUserFormInstanceKey}
      >
        <RadioGroup>
          <RadioButton
            label={t('For women')}
            {...subscriptionForm.register('gender')}
            value={'female'}
            id="newsletter-gender-female"
          />
          <RadioButton
            label={t('For men')}
            {...subscriptionForm.register('gender')}
            value={'male'}
            id="newsletter-gender-male"
          />
        </RadioGroup>

        <TextInputWrapper>
          <TextInput
            type="email"
            autoComplete="email"
            value={subscriptionForm.getValues('email')}
            errorMessage={subscriptionForm.formState.errors.email?.message}
            label={t('Your email address')}
            {...subscriptionForm.register('email', {
              required: t('Please enter your email address'),
              pattern: {
                value: emailRegex,
                message: t('This is not a valid email address'),
              },
            })}
            activeStyle={{
              backgroundColor: COLORS.White,
              borderColor: COLORS.Black,
            }}
          />
        </TextInputWrapper>
        <StyledButton>{t('Sign up')}</StyledButton>
      </form>
      <Disclaimer
        text={t(
          'I would like to receive newsletters from ABOUT YOU about current trends, offers and vouchers in accordance with the {privacyPolicy}. You can withdraw your consent at any time with effect for the future by sending a message to {email} or using the unsubscribe option at the end of each newsletter.',
          {
            privacyPolicy: `<a id="policyLink" rel="noopener noreferrer" target="_blank" href="${privacyPolicyLinkSection}">${t(
              'Privacy Policy',
            )}</a>`,
            email: `<a href="mailto:${customerEmail}">${customerEmail}</a>`,
          },
        )}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 1.5rem 1.5rem 3rem 1.5rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 768px;
  margin: auto;

  form {
    display: contents;
  }
`;

const TextInputWrapper = styled.div`
  padding: 1rem;
  width: 100%;
`;

const StyledText = styled(Text)`
  padding-bottom: 1rem;
  text-align: center;
  line-height: 1.3;
`;

const StyledButton = styled(Button)`
  margin-bottom: 2rem;
  &&& {
    width: calc(100% - 32px);
  }
`;
